import { Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly';
import type { ApplePayConfig, RecurlyError, TokenPayload } from '@recurly/recurly-js';
import classNames from 'classnames';
import type { userAgent } from 'next/server';
import { ComponentProps, ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import NoSsr from '@alltrails/core/components/NoSsr';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { ApiError } from '@alltrails/core/utils/api';
import { getCopyrightText } from '@alltrails/core/utils/getCopyrightText';
import { executeRecaptcha } from '@alltrails/core/utils/recaptcha-helpers';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type PaymentTokenPayload from '../../types/PaymentTokenPayload';
import type ProResponse from '../../types/ProResponse';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import ComplianceCheckbox from '../ComplianceCheckbox/ComplianceCheckbox';
import useTokenizedFormValidation from '../TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import styles from './Checkout.module.scss';
import CheckoutModalFooter from './CheckoutModalFooter';
import CouponCode from './CouponCode';
import PaymentForm from './PaymentForm';
import RadioPaymentButtons from './RadioPaymentButtons';
import { PaymentOptionValues } from './RadioPaymentForm/PaymentOptions';
import RadioPaymentForm from './RadioPaymentForm/RadioPaymentForm';
import StripeCheckoutBase from './StripeCheckoutBase';
import TogglePaymentButton from './TogglePaymentButton/TogglePaymentButton';
import TrialReminder from './TrialReminder';

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

const isApplePayUserAgent = (value: string) => {
  const lowerCasedUserAgent = value.toLowerCase();

  return (
    lowerCasedUserAgent.includes('macintosh') ||
    lowerCasedUserAgent.includes('iphone') ||
    lowerCasedUserAgent.includes('ipad') ||
    lowerCasedUserAgent.includes('ipod')
  );
};

type Props = {
  applePayConfig: ApplePayConfig;
  ariaLabelledBy?: string;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingClassName?: string;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  canUpgradeExistingSubscription?: boolean;
  className?: string;
  coupon?: Coupon;
  couponError?: string;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  isComplianceCheckboxEnabled?: boolean;
  isMultiyearConversion?: boolean;
  isUpgrade?: boolean;
  isGooglePayAvailable?: boolean;
  /** @deprecated Use onPaymentToken with payment_method_type 'apple_pay' instead */
  onApplePayToken: ComponentProps<typeof PaymentForm>['onApplePayToken'];
  onBillingCountryChange: (countryCode: CountryCode, response: any) => void;
  /** @deprecated Use onPaymentToken with payment_method_type 'google_pay' instead */
  onGooglePayToken: ComponentProps<typeof PaymentForm>['onGooglePayToken'];
  /** @deprecated Use onPaymentToken with payment_method_type 'paypal' instead */
  onPayPalToken: ComponentProps<typeof PaymentForm>['onPayPalToken'];
  onPaymentToken: (payload: PaymentTokenPayload) => void | Promise<void>;
  /** @deprecated Use onPaymentToken with payment_method_type 'card' instead */
  onSubmitTokenizedCardForm: (payload: { cc_token?: string; recaptcha: string }) => void;
  plan: Plan;
  promoText?: ReactNode;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  subscription?: SubscriptionsNewResponse['subscription'];
  webProPurchaseLocation: WebProPurchaseLocation;
  whenExpired?: string;
  purchaseTier: PurchaseTier;
} & ({ isCheckoutModal: true; features?: never; trigger: UpsellTrigger } | { isCheckoutModal?: never; features: ReactNode; trigger?: never });

function CheckoutBase({
  applePayConfig,
  ariaLabelledBy,
  buttonText,
  billedHeading,
  billedToday,
  billedSubheading,
  billedSubheadingClassName,
  billingCountry,
  className,
  coupon,
  couponError,
  currencyCode,
  finePrint,
  promoText,
  heading,
  headingClassName,
  isComplianceCheckboxEnabled = false,
  isCheckoutModal,
  isUpgrade,
  isGooglePayAvailable = false,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  subheading,
  billedSubheadingRight,
  setCoupon,
  setPlanToAnnual,
  webProPurchaseLocation,
  features,
  onBillingCountryChange,
  onSubmitTokenizedCardForm,
  purchaseTier,
  trigger
}: Props) {
  const reactRecurlyInstance = useRecurly();
  const intl = useIntl();

  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isChrome = browserName?.includes('Chrome');
  const searchParams = useClientNavigationSearchParams();

  const isRadioUiEnabled = !isChrome;
  const isMobile = useIsMobileSizedScreen();
  const isIosNativeApp = isMobile && searchParams.get('app_source') === 'iOS';
  const isIos14DayTrialTreatmentEnabled = isIosNativeApp && purchaseTier === 'plus' && searchParams.get('lt14') === 'true';

  // Feature flag for compliance checkbox validation on third-party payment buttons
  const experiments = useExperiments();
  const stripeCheckoutVariant = experiments['web-growth-rm-stripe-elements-checkout-v2']?.value;
  const isRecurlyReducedApplePayVariant = stripeCheckoutVariant === 'recurly_reduced_apple_pay';
  const isApplePayEnabled =
    !isRecurlyReducedApplePayVariant ||
    (typeof navigator !== 'undefined' && isApplePayUserAgent(navigator.userAgent));
  const isComplianceValidationEnabled = experiments['web-growth-rm-california-checkout-page-compliance-jan-2026']?.value === 'treatment';

  const [submittingCreditCard, setSubmittingCreditCard] = useState(false);
  const isFinePrintLeftAligned = billingCountry === 'US' && !isCheckoutModal && !isMobile && !isComplianceCheckboxEnabled;
  // non-credit card submitting that controls the overlay
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<ProResponse['errors']>();
  const formRef = useRef<HTMLFormElement>(null);
  const { validationErrors, setClientValidationErrors, setTokenRequestErrors, hasValidationErrors } = useTokenizedFormValidation();

  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentOptionValues>();

  const [isComplianceChecked, setIsComplianceChecked] = useState(false);
  const [complianceError, setComplianceError] = useState(false);

  const sendCaliforniaCheckoutPageComplianceExposure = useExposureEvent('web-growth-rm-california-checkout-page-compliance-jan-2026');

  // CA compliance validation function for third-party payment buttons (Apple Pay, Google Pay, PayPal)
  // Credit card validation always runs; third-party payments respect feature flag
  const validateCompliance = (paymentMethod: PaymentMethod): boolean => {
    if (paymentMethod !== 'credit_card' && !isComplianceValidationEnabled) {
      return true; // Bypass validation if feature flag is off (third-party payments only)
    }

    // Clear any existing error first
    if (complianceError) {
      setComplianceError(false);
    }

    const hasComplianceError = isComplianceCheckboxEnabled && !isComplianceChecked;

    if (hasComplianceError) {
      setComplianceError(true);
      logPurchaseFailed({
        error_code: 'terms and conditions unchecked',
        payment_method: paymentMethod
      });
    }

    return !hasComplianceError; // Returns true if valid, false if blocked
  };

  // Default payment type to Apple Pay for iOS native app checkout.
  useEffect(() => {
    if (isIosNativeApp) {
      setSelectedPaymentType(PaymentOptionValues.Apple);
    }
  }, [isIosNativeApp]);

  useEffect(() => {
    if (isComplianceCheckboxEnabled) {
      sendCaliforniaCheckoutPageComplianceExposure();
    }
  }, [isComplianceCheckboxEnabled, sendCaliforniaCheckoutPageComplianceExposure]);

  const handleTokenizedCardSubmit = async (e: React.KeyboardEvent | React.FormEvent) => {
    e.preventDefault();

    const isGiftCard = coupon?.type === 'GiftCard';

    if (submittingCreditCard) {
      return;
    }

    // Validate compliance checkbox (always runs for credit cards, regardless of feature flag)
    const isComplianceValidated = validateCompliance('credit_card');

    // Skip validation for gift cards.
    if ((hasValidationErrors() || !isComplianceValidated) && !isGiftCard) {
      return;
    }

    const onRecurlyToken = async (err: RecurlyError | null, token: TokenPayload, recaptcha: string) => {
      if (err) {
        logPurchaseFailed({ error_code: err.message, payment_method: 'credit_card' });
        setTokenRequestErrors(err);
        setSubmittingCreditCard(false);
      } else {
        const body = {
          recaptcha,
          cc_token: token.id
        };

        try {
          await onSubmitTokenizedCardForm(body);
        } catch (e) {
          logPurchaseFailed({
            ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
            payment_method: 'credit_card'
          });

          if (e instanceof ApiError) {
            setErrors(e.data);
          }

          setSubmittingCreditCard(false);
        }
      }
    };

    let recaptcha; // : string | null = null;

    try {
      setSubmittingCreditCard(true);
      recaptcha = await executeRecaptcha('userPaymentAction');

      if (isGiftCard) {
        await onSubmitTokenizedCardForm({ recaptcha });
        setSubmittingCreditCard(false);
        return;
      }

      if (recaptcha) {
        reactRecurlyInstance.token(formRef.current!, (err, token) => onRecurlyToken(err, token, recaptcha));
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.data);
      }
      // else {
      //   setErrors(e);
      // }
      setSubmittingCreditCard(false);
    }
  };

  return (
    <form
      ref={formRef}
      className={classNames(styles.content, className, {
        [styles.complianceCheckboxEnabled]: isComplianceCheckboxEnabled,
        [styles.iosWebCheckoutOptimizationEnabled]: isIosNativeApp,
        [styles.buttonToggleTreatmentVariant]: isIosNativeApp,
        [styles.hierarchyCardCtaAndTimelineVariant]: isIosNativeApp,
        [styles.isAppleCheckout]: selectedPaymentType !== PaymentOptionValues.CreditCard
      })}
      onSubmit={(e: React.KeyboardEvent | React.FormEvent) => {
        handleTokenizedCardSubmit(e);
      }}
    >
      <div className={styles.columnContainer}>
        <div className={styles.tryArea}>
          <h1
            className={classNames(styles.heading, headingClassName, {
              [styles.complianceHeading]: isComplianceCheckboxEnabled
            })}
            id={ariaLabelledBy}
          >
            {heading}
          </h1>

          {isRadioUiEnabled && <SectionDivider classes={styles.headingDivider} />}
          {isIosNativeApp && plan.isTrial && (
            <div className={styles.timeline}>
              <TrialReminder
                isIos14DayTrialTreatmentEnabled={isIos14DayTrialTreatmentEnabled}
                currencyCode={currencyCode}
                cost={plan.priceTotal}
                tier={purchaseTier}
                selected={true}
              />
            </div>
          )}
          {subheading && (
            <div className={styles.subheading}>
              <Typography variant="text100">{subheading}</Typography>
            </div>
          )}
          {features}
        </div>
        <div className={styles.billedCopyright}>
          <div className={styles.billedArea}>
            <div className={styles.headings}>
              <div className={styles.billed}>
                <Typography component="div" variant="text200bold">
                  {billedHeading || <FormattedMessage defaultMessage="Billed today" />}
                </Typography>
                <Typography component="div" variant="text200">
                  {billedToday}
                </Typography>
              </div>
              <div className={styles.hr} />
              {billedSubheading && (
                <div className={classNames(styles.after, billedSubheadingClassName)}>
                  <Typography component="div" variant="text200bold">
                    {billedSubheading}
                  </Typography>
                  <Typography component="div" variant="text200">
                    {billedSubheadingRight}
                  </Typography>
                </div>
              )}
            </div>
            {/* Hide coupon code if any treatment variant is enabled for the iOS web checkout optimization experiment */}
            {setCoupon && setPlanToAnnual && !isIosNativeApp && (
              <div>
                <CouponCode
                  couponError={couponError}
                  coupon={coupon}
                  purchaseTier={purchaseTier}
                  setCoupon={setCoupon}
                  setPlanToAnnual={setPlanToAnnual}
                />
              </div>
            )}
          </div>
          {finePrint && isFinePrintLeftAligned && <div className={classNames(styles.fine, styles.leftAlign)}>{finePrint}</div>}
          {isComplianceCheckboxEnabled && (
            <ComplianceCheckbox
              finePrint={finePrint}
              checked={isComplianceChecked}
              onChange={checked => {
                setIsComplianceChecked(checked);
                setComplianceError(false);
              }}
              error={complianceError}
            />
          )}
          <div
            className={classNames(styles.copyright, {
              [styles.leftAlign]: isFinePrintLeftAligned,
              [styles.complianceVariant]: isComplianceCheckboxEnabled
            })}
          >
            {getCopyrightText({ intl })}
            <br />
            <div>
              <Link className={styles.link} href="/privacy" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Privacy Policy" />
              </Link>{' '}
              |{' '}
              <Link className={styles.link} href="/terms" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Terms of Service" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.columnContainer}>
        <div
          className={classNames(styles.form, {
            [styles.radioFormContainer]: isRadioUiEnabled,
            [styles.complianceEnabled]: isComplianceCheckboxEnabled
          })}
        >
          {isRadioUiEnabled ? (
            <RadioPaymentForm
              billingCountry={billingCountry}
              coupon={coupon}
              currencyCode={currencyCode}
              errors={errors}
              isApplePayEnabled={isApplePayEnabled}
              isGooglePayAvailable={isGooglePayAvailable}
              isUpgrade={isUpgrade}
              onApplePayToken={onApplePayToken}
              onBillingCountryChange={onBillingCountryChange}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              selectedPaymentType={selectedPaymentType}
              setClientValidationErrors={setClientValidationErrors}
              setErrors={setErrors}
              setSelectedPaymentType={setSelectedPaymentType}
              submitting={submitting}
              isIosNativeApp={isIosNativeApp}
              validationErrors={validationErrors}
            />
          ) : (
            <PaymentForm
              applePayConfig={applePayConfig}
              billingCountry={billingCountry}
              browserName={browserName}
              coupon={coupon}
              currencyCode={currencyCode}
              errors={errors}
              isApplePayEnabled={isApplePayEnabled}
              isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
              isUpgrade={isUpgrade}
              onBillingCountryChange={onBillingCountryChange}
              onApplePayToken={onApplePayToken}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              setClientValidationErrors={setClientValidationErrors}
              setErrors={setErrors}
              validationErrors={validationErrors}
              webProPurchaseLocation={webProPurchaseLocation}
              validateCompliance={validateCompliance}
            />
          )}
          {isCheckoutModal && (
            <CheckoutModalFooter
              applePayConfig={applePayConfig}
              coupon={coupon}
              currencyCode={currencyCode}
              isApplePayEnabled={isApplePayEnabled}
              isBaseVariant={!isRadioUiEnabled}
              onApplePayToken={onApplePayToken}
              onGooglePayToken={onGooglePayToken}
              onPayPalToken={onPayPalToken}
              plan={plan}
              purchaseTier={purchaseTier}
              selectedPaymentType={selectedPaymentType}
              setErrors={setErrors}
              setSubmitting={setSubmitting}
              submitting={submitting}
              submittingCreditCard={submittingCreditCard}
              trigger={trigger}
              webProPurchaseLocation="modal"
              validateCompliance={validateCompliance}
            />
          )}
        </div>
        {!isCheckoutModal && (
          <div className={styles.buttonContainer}>
            {isRadioUiEnabled ? (
              <RadioPaymentButtons
                applePayConfig={applePayConfig}
                buttonClassName={styles.button}
                creditCardButtonText={isCheckoutModal ? <FormattedMessage defaultMessage="Continue" /> : buttonText}
                coupon={coupon}
                currencyCode={currencyCode}
                isApplePayEnabled={isApplePayEnabled}
                onApplePayToken={onApplePayToken}
                onGooglePayToken={onGooglePayToken}
                onPayPalToken={onPayPalToken}
                plan={plan}
                purchaseTier={purchaseTier}
                selectedPaymentType={selectedPaymentType}
                setErrors={setErrors}
                setSubmitting={setSubmitting}
                submitting={submitting}
                submittingCreditCard={submittingCreditCard}
                trigger={trigger}
                webProPurchaseLocation={webProPurchaseLocation}
                validateCompliance={validateCompliance}
              />
            ) : (
              <Button
                className={styles.button}
                loading={submittingCreditCard}
                onClick={() => {
                  logPurchaseButtonTapped({
                    payment_method: 'credit_card',
                    product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
                    purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
                    web_purchase_location: webProPurchaseLocation
                  });
                }}
                text={buttonText}
                type="submit"
                variant="primary"
              />
            )}
            {isIosNativeApp && <TogglePaymentButton selectedPaymentType={selectedPaymentType} setSelectedPaymentType={setSelectedPaymentType} />}
          </div>
        )}
        {finePrint && !isFinePrintLeftAligned && !isComplianceCheckboxEnabled && <div className={styles.fine}>{finePrint}</div>}
        {promoText && <div className={styles.promoText}>{promoText}</div>}
      </div>
    </form>
  );
}

function CheckoutBaseProvider(props: Props) {
  const experiments = useExperiments();
  const stripeCheckoutVariant = experiments['web-growth-rm-stripe-elements-checkout-v2']?.value;
  const isStripeTreatment = stripeCheckoutVariant === 'treatment';
  const linkCheckoutVariant = experiments['web-growth-rm-link-checkout']?.value;
  const isLinkExpressCheckoutEnabled = linkCheckoutVariant === 'link_express' || linkCheckoutVariant === 'link_both';
  const isLinkPaymentElementEnabled = linkCheckoutVariant === 'link_payment_element' || linkCheckoutVariant === 'link_both';
  const isStripeCheckoutEnabled =
    isStripeTreatment || linkCheckoutVariant === 'control' || isLinkExpressCheckoutEnabled || isLinkPaymentElementEnabled;
  const isStripeCheckoutEligible = !props.isComplianceCheckboxEnabled && !props.canUpgradeExistingSubscription && !props.isMultiyearConversion;
  const sendStripeCheckoutExposure = useExposureEvent('web-growth-rm-stripe-elements-checkout-v2');
  const sendLinkCheckoutExposure = useExposureEvent('web-growth-rm-link-checkout');

  useEffect(() => {
    if (isStripeCheckoutEligible) {
      sendStripeCheckoutExposure();
    }
  }, [isStripeCheckoutEligible, sendStripeCheckoutExposure]);

  useEffect(() => {
    if (isStripeCheckoutEligible) {
      sendLinkCheckoutExposure();
    }
  }, [isStripeCheckoutEligible, sendLinkCheckoutExposure]);

  if (isStripeCheckoutEnabled && isStripeCheckoutEligible) {
    return (
      <StripeCheckoutBase
        ariaLabelledBy={props.ariaLabelledBy}
        billedHeading={props.billedHeading}
        billedSubheading={props.billedSubheading}
        billedSubheadingClassName={props.billedSubheadingClassName}
        billedSubheadingRight={props.billedSubheadingRight}
        billedToday={props.billedToday}
        billingCountry={props.billingCountry}
        buttonText={props.buttonText}
        canUpgradeExistingSubscription={props.canUpgradeExistingSubscription ?? false}
        className={props.className}
        coupon={props.coupon}
        couponError={props.couponError}
        currencyCode={props.currencyCode}
        finePrint={props.finePrint}
        heading={props.heading}
        headingClassName={props.headingClassName}
        isComplianceCheckboxEnabled={props.isComplianceCheckboxEnabled}
        isLinkExpressCheckoutEnabled={isLinkExpressCheckoutEnabled}
        isLinkPaymentElementEnabled={isLinkPaymentElementEnabled}
        isMultiyearConversion={props.isMultiyearConversion}
        isUpgrade={props.isUpgrade}
        onBillingCountryChange={props.onBillingCountryChange}
        onSubmitTokenizedCardForm={props.onSubmitTokenizedCardForm}
        onPaymentToken={props.onPaymentToken}
        plan={props.plan}
        promoText={props.promoText}
        purchaseTier={props.purchaseTier}
        setCoupon={props.setCoupon}
        setPlanToAnnual={props.setPlanToAnnual}
        subheading={props.subheading}
        subscription={props.subscription}
        webProPurchaseLocation={props.webProPurchaseLocation}
        whenExpired={props.whenExpired}
        {...(props.isCheckoutModal
          ? { isCheckoutModal: true as const, trigger: props.trigger }
          : { isCheckoutModal: undefined, features: props.features })}
      />
    );
  }

  return (
    <NoSsr fallback={null}>
      {/* Note: RecurlyProvider isn't a Suspense-enabled component, so Suspense does nothing and "fallback" isn't rendered. It can be removed */}
      <Suspense fallback={null}>
        {/* RecurlyProvider can't be server rendered */}
        <RecurlyProvider publicKey={process.env.NEXT_PUBLIC_RECURLY_KEY}>
          <Elements>
            <CheckoutBase {...props} />
          </Elements>
        </RecurlyProvider>
      </Suspense>
    </NoSsr>
  );
}

export default CheckoutBaseProvider;
